import { type StorePrivacy } from 'ecosystem'
import { type ConsentState } from '../../typings'

export interface CMPConfig {
  cmpType: StorePrivacy['cmpType']
  cmpId?: StorePrivacy['cmpId']
  isDebugMode?: boolean
  /**
   * Every data layer consent may arrive on. A CMP running inside a destination
   * container writes to that container's own layer, not to `window.dataLayer`.
   */
  dataLayerNames?: string[]
}

type CMPManagementMode = 'managed' | 'self'

export abstract class BaseCMP {
  protected config: CMPConfig
  protected listeners: (() => void)[] = []
  protected initialized = false
  protected consentUpdateCallback?: (state: ConsentState) => void

  constructor(config: CMPConfig) {
    this.config = config
  }

  abstract initialize(): void
  abstract getConsentState(): ConsentState | null
  abstract destroy(): void

  protected abstract setupEventListeners(): void
  protected abstract handleConsentUpdate(): void

  public setConsentUpdateCallback(callback: (state: ConsentState) => void): void {
    this.consentUpdateCallback = callback
  }

  public isInitialized(): boolean {
    return this.initialized
  }

  protected log(message: string, ...args: any[]): void {
    if (this.config.isDebugMode) {
      console.log(`[${this.config.cmpType}] ${message}`, ...args)
    }
  }

  protected logError(message: string, error?: any): void {
    if (this.config.isDebugMode) {
      console.error(`[${this.config.cmpType}] ${message}`, error)
    }
  }

  protected shouldInstallCMP(): boolean {
    return Boolean(this.config.cmpId)
  }

  protected getManagementMode(): CMPManagementMode {
    if (this.config.cmpId) {
      return 'managed' // Install and manage the CMP
    }
    return 'self' // Assume CMP is already installed, just listen to events
  }
}
